import { type ComponentProps, memo, useState } from "react"
import {
  Toolbar,
  type ToolbarProps,
  IconButton,
  Button,
  Badge,
  Box,
} from "@mui/material"
import {
  AppsRounded,
  FormatListBulletedRounded,
  ListRounded,
  SortRounded,
  TuneRounded,
} from "@mui/icons-material"
import { SortByDialog } from "../../books/lists"

export type ListActionSorting = ComponentProps<typeof SortByDialog>["value"]
export type ListActionViewMode = "grid" | "list" | "compact" | "horizontal"

export const ViewModeIconButton = ({
  viewMode,
  onViewModeChange,
}: {
  viewMode: ListActionViewMode
  onViewModeChange?: (viewMode: ListActionViewMode) => void
}) => {
  return (
    <IconButton
      color="primary"
      onClick={() => {
        const newViewMode =
          viewMode === "compact"
            ? "grid"
            : viewMode === "list"
              ? "compact"
              : "list"

        onViewModeChange?.(newViewMode)
      }}
      size="large"
    >
      {viewMode === "grid" ? (
        <AppsRounded />
      ) : viewMode === "list" ? (
        <FormatListBulletedRounded />
      ) : (
        <ListRounded />
      )}
    </IconButton>
  )
}

export const ListActionsToolbar = memo(function ListActionsToolbar({
  viewMode,
  onViewModeChange,
  onSortingChange,
  sorting,
  onFilterClick,
  numberOfFiltersApplied = 0,
  ...rest
}: {
  viewMode?: ListActionViewMode
  sorting?: ListActionSorting
  onViewModeChange?: (viewMode: ListActionViewMode) => void
  onSortingChange?: (sorting: ListActionSorting) => void
  numberOfFiltersApplied?: number
  onFilterClick?: () => void
} & ToolbarProps) {
  const [isSortingDialogOpened, setIsSortingDialogOpened] = useState(false)

  return (
    <>
      <Toolbar
        style={{
          boxSizing: "border-box",
        }}
        {...rest}
      >
        <Box
          sx={{
            flexGrow: 1,
          }}
        >
          {!!onFilterClick && (
            <IconButton
              edge="start"
              onClick={() => onFilterClick?.()}
              size="large"
              color="primary"
            >
              {numberOfFiltersApplied > 0 ? (
                <Badge badgeContent={numberOfFiltersApplied}>
                  <TuneRounded />
                </Badge>
              ) : (
                <TuneRounded />
              )}
            </IconButton>
          )}
          {!!sorting && (
            <div
              style={{
                justifyContent: "flex-start",
                flexFlow: "row",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Button
                variant="text"
                onClick={() => setIsSortingDialogOpened(true)}
                startIcon={<SortRounded />}
              >
                {sorting === "activity"
                  ? "Recent activity"
                  : sorting === "alpha"
                    ? "A > Z"
                    : "Date added"}
              </Button>
            </div>
          )}
        </Box>
        {!!viewMode && (
          <ViewModeIconButton
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
          />
        )}
      </Toolbar>
      <SortByDialog
        value={sorting}
        onClose={() => setIsSortingDialogOpened(false)}
        open={isSortingDialogOpened}
        onChange={onSortingChange}
      />
    </>
  )
})
